import { BrowserRouter, Route, Routes } from "react-router-dom";
import Home from "../views/Home";
import Authenticate from "../views/Authenticate";
import BaseItemIndex from "../views/BaseItem";
import BaseShowItem from "../views/BaseItem/show";
import BaseCreateItem from "../views/BaseItem/create";
import BaseUpdateItem from "../views/BaseItem/update";
import { NavbarLayout } from "./NavbarLayout";
import { ErrorPage, ModelErrorPage } from "../views/ErrorPages";
import BaseSingleItem from "../views/BaseSingleItem";
import UpdateSingleItem from "../views/BaseSingleItem/update";
import Logout from "../views/Logout";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { generalDataAtom, loginStatusAtom, userAtom } from "../atoms";
import Settings from "../views/Settings";
import ResetPassword from "../views/ResetPassword";
import RequestReset from "../views/RequestReset";
import UserIndex from "../views/Users";
import CreateUser from "../views/Users/create";
import ShowUser from "../views/Users/show";
import UpdateUser from "../views/Users/update";
import { MFAAuth } from "../views/MFAAuth";
import RequestMFAReset from "../views/RequestMFAReset";
import ResetMFA from "../views/ResetMFA";
import { Analytics } from "../views/Analytics";
import SettingsIndex from "../views/AppSettings";
import UpdateOptions from "../views/AppSettings/updateOptions";
import MomentIndex from "../views/Moment";
import ShowMedia from "../views/Media/show";
import apiAgent from "../api/apiAgent";
import { useEffect, useState } from "react";
import { Loader } from "../components/Loader";

const PrivatePages = (): JSX.Element => {
  const loginStatus = useAtomValue(loginStatusAtom);
  return loginStatus ? <NavbarLayout /> : <Logout />;
};

const AppRoutes = () => {
  const [isValidating, setIsValidating] = useState<boolean>(true);
  const setLoginStatus = useSetAtom(loginStatusAtom);
  const [user, setUser] = useAtom(userAtom);
  const generalData = useAtomValue(generalDataAtom);
  const showUserMgmt =
    user?.user_role === "admin" && generalData.enable_user_management;

  const checkValidate = async () => {
    try {
      const validateResponse = await apiAgent.Auth.validate();
      if (validateResponse.status === 200) {
        setLoginStatus(true);
        setUser(validateResponse.data || null);
      } else if (validateResponse.status === 401) {
        setLoginStatus(false);
        throw "Not logged in";
      }
    } catch (err) {
      // console.log(err);
      setLoginStatus(false);
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    checkValidate();
  }, []);

  if (isValidating)
    return (
      <div className="w-full h-full grid content-center">
        <Loader />
      </div>
    );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth" element={<Authenticate />} />
        <Route path="/login" element={<Authenticate />} />
        <Route path="/mfa_auth" element={<MFAAuth />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/another_hidden_signup_page" element={<Settings />} />
        <Route path="/request_reset" element={<RequestReset />} />
        <Route path="/request_mfa_reset" element={<RequestMFAReset />} />
        <Route path="/reset_password" element={<ResetPassword />} />
        <Route path="/reset_mfa" element={<ResetMFA />} />
        <Route path="*" element={<ErrorPage />} />
        <Route element={<PrivatePages />}>
          <Route path="/test" element={<Home />} />
          <Route index element={<MomentIndex modelName="moment" />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route
            path="/m/moment"
            element={<MomentIndex modelName="moment" />}
          />
          <Route
            path="/m/landmark"
            element={<MomentIndex modelName="landmark" />}
          />
          <Route path="/m/media/show/:id" element={<ShowMedia />} />
          <Route path="/m/:model" element={<BaseItemIndex />} />
          <Route path="/m/:model/create" element={<BaseCreateItem />} />
          <Route path="/m/:model/edit/:id" element={<BaseUpdateItem />} />
          <Route path="/m/:model/show/:id" element={<BaseShowItem />} />
          <Route path="/si/settings" element={<SettingsIndex />} />
          <Route
            path="/si/topics/edit"
            element={<UpdateOptions itemName="topics" />}
          />
          <Route
            path="/si/place/edit"
            element={<UpdateOptions itemName="place" />}
          />
          <Route path="/si/:item" element={<BaseSingleItem />} />
          <Route path="/si/:item/edit" element={<UpdateSingleItem />} />
          <Route path="/m/error" element={<ModelErrorPage />} />
          <Route
            path="/m/users/"
            element={showUserMgmt ? <UserIndex /> : <ModelErrorPage />}
          />
          <Route
            path="/m/users/create"
            element={showUserMgmt ? <CreateUser /> : <ModelErrorPage />}
          />
          <Route
            path="/m/users/show/:id"
            element={showUserMgmt ? <ShowUser /> : <ModelErrorPage />}
          />
          <Route
            path="/m/users/edit/:id"
            element={showUserMgmt ? <UpdateUser /> : <ModelErrorPage />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
